/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, { useState, useLayoutEffect, useEffect } from 'react';
import {
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	FormGroup,
	IconButton,
	MenuItem,
	MenuList, SvgIcon,
	TextField,
	Typography
} from '@mui/material';
import { connect } from 'react-redux';
import AddIcon from '@mui/icons-material/esm/Add';
import SheetIcon from '@mui/icons-material/esm/GridOn';
import DashboardIcon from '@mui/icons-material/esm/Dashboard';
import CloseIcon from '@mui/icons-material/esm/Close';
import JSG from '@cedalo/jsg-ui';
import { FormattedMessage } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import MoreVertIcon from '@mui/icons-material/esm/MoreVert';
import ReorderIcon from '@mui/icons-material/esm/Reorder';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/esm/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/esm/KeyboardArrowUp';
import * as Actions from '../../actions/actions';
import { graphManager } from '../../GraphManager';
import ConfirmDialog from '../admin/ConfirmDialog';
import {useAsyncReference, useJSGNotification} from '../utils/JSGHooks';
import TreeContextMenu from './contextmenus/TreeContextMenu';
import GraphContextMenu from './contextmenus/GraphContextMenu';
import LayoutContextMenu from './contextmenus/LayoutContextMenu';
import EditPointsContextMenu from './contextmenus/EditPointsContextMenu';
import AlertDialog from './dialogs/AlertDialog';
import DecisionDialog from './dialogs/DecisionDialog';
import SheetDeleteDialog from './dialogs/SheetDeleteDialog';
import { intl } from '../../helper/IntlGlobalProvider';
import { license } from '../../utils';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ArrowDropDown } from '@mui/icons-material';
import Popover from '@mui/material/Popover';
import { bindActionCreators } from 'redux';
import SheetContextMenu from './contextmenus/SheetContextMenu';
import { withUser } from '../../UserProvider';
import { withAppData } from '../app/AppProvider';
import SheetTabsList from './SheetTabsList';
import CellPayloadPopover from './CellPayloadPopover';


const CanvasHolder = (props) => {
	const [parent, setParent] = useState();
	useLayoutEffect(() => {
		if (parent) {
			parent.appendChild(graphManager.getCanvas());
			graphManager.updateDimensions();
		}
	}, [parent]);
	return <div ref={(ref) => setParent(ref)} {...props} />;
};

const getNextIndex = (fromIndex, direction, container) => {
	const stremasheets = container.streamSheetsModel;
	let sheet;
	let toIndex = fromIndex;
	do {
		toIndex += direction;
		sheet = stremasheets[toIndex];
		if (!sheet || sheet.isVisible) break;
	} while (toIndex > -1 || toIndex < stremasheets.length);
	return toIndex;
};
const getSheetIndex = (sheet, container) => (sheet && container ? container.streamSheetsModel.indexOf(sheet) : -1);
const hasLeftSheet = (sheet, container) => {
	const index = getSheetIndex(sheet, container);
	const nextIndex = getNextIndex(index, -1, container);
	return index !== nextIndex && nextIndex > -1;
};
const hasRightSheet = (sheet, container) => {
	const index = getSheetIndex(sheet, container);
	const nextIndex = getNextIndex(index, 1, container);
	return index !== nextIndex && nextIndex < container.streamSheetsModel.length;
};

const countVisibleSheets = (total, sheet) => (sheet.isVisible ? total + 1 : total);
const canHideSheet = (container) => container && container.streamSheetsModel.reduce(countVisibleSheets, 0) > 1;

const connectionStatus = (connections, sheetId) =>
	connections
		.filter((c) => c.targets.includes(sheetId))
		.map(({ name, type, status = { isConnected: false } }) => ({ name, type, isConnected: status ? status.isConnected : false}));

function SheetComponent(props) {
	const [sheetContainer, setSheetContainer] = useState();
	const [sheet, setSheet] = useState();
	const [, setRandom] = useState(Math.random());
	const [speedDialOpenLeft, setSpeedDialOpenLeft] = useState(false);
	const [speedDialOpenRight, setSpeedDialOpenRight] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchOptions, setSearchOptions] = useState(false);
	const [searchCaps, setSearchCaps] = useState(false);
	// const [searchCount, setSearchCount] = useState(false);
	const [searchFormulas, setSearchFormulas] = useState(false);
	const [searchActiveSheet, setSearchActiveSheet] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [showTabContextMenu, setShowTabContextMenu] = useState(false);
	const [showDeleteSheetDialog, setShowDeleteSheetDialog] = useState(false);
	const [verifyShapeFormulas, setVerifyShapeFormulas] = useState(false);
	const [shapeCommand, setShapeCommand] = useState();
	const [anchorEl, setAnchorlEl] = useState(null);
	const [sheetAddedTo, setSheetAddedTo] = useAsyncReference();
	const [sheetInsertAt, setSheetInsertAt] = useAsyncReference();
	const [tabsListEl, setTabListEl] = useState(null);
	const [payloadInfo, setPayloadInfo] = useState({ open: false, anchor: undefined });
	const { showViewMode, viewSettings } = props;
	const canEdit = props.canEditMachine;

	const updateDimensions = () => {
		graphManager.updateDimensions();
		graphManager.redraw();
	};

	const initGraphEditor = () => {
		updateDimensions();
		graphManager.setUserSelectionId(props.user);

		const viewer = graphManager.getGraphViewer();
		const leftSheet = graphManager.graph.streamSheetsContainer.leftSheetContainer.getStreamSheet();
		const controller = viewer.getGraphController().getControllerByModelId(leftSheet.getId());
		controller.getView().notifySelectionChange(viewer);

		return graphManager.getGraphEditor();
	};

	useEffect(() => {
		initGraphEditor();
		document.fonts.ready.then(() => {
			graphManager.redraw();
		});
		return () => {
			graphManager.getGraphEditor().destroy();
		};
	}, []);

	useEffect(() => {
		const { graph } = graphManager;
		const viewer = graphManager.getGraphViewer();
		const graphController = viewer.getGraphController();
		const leftSheet = graph.streamSheetsContainer.leftSheetContainer.getStreamSheet();
		const controller = graphController.getControllerByModelId(leftSheet.getId());
		const view = controller.getView();

		if (leftSheet) {
			if (showViewMode) {
				const index = Math.max(0, graph.streamSheetsContainer.findIndex(viewSettings.maximize));
				graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(index, viewer);
				if (viewSettings.allowScroll === false) {
					leftSheet.setHorizontalScrollbarMode(JSG.ScrollBarMode.HIDDEN);
					leftSheet.setVerticalScrollbarMode(JSG.ScrollBarMode.HIDDEN);
				} else {
					leftSheet.setHorizontalScrollbarMode(JSG.ScrollBarMode.AUTO);
					leftSheet.setVerticalScrollbarMode(JSG.ScrollBarMode.AUTO);
				}
				leftSheet.layout();
				view.layout();
				graphManager.removeSelection(leftSheet.getSelectionId());
			} else {
				leftSheet.setHorizontalScrollbarMode(JSG.ScrollBarMode.AUTO);
				leftSheet.setVerticalScrollbarMode(JSG.ScrollBarMode.AUTO);
				leftSheet.layout();
				view.layout();
			}
		}

		graph.setRefreshNeeded(true);
		graph.markDirty();

	}, [showViewMode]);

	useEffect(() => {
		if (!showViewMode) {
			return;
		}

		const { graph } = graphManager;
		const viewer = graphManager.getGraphViewer();
		const index = Math.max(0, graph.streamSheetsContainer.findIndex(viewSettings.maximize));
		graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(index, viewer);

		graph.markDirty();

	}, [viewSettings]);

	const onZoom = () => {
		const canvas = graphManager.getCanvas();
		if (!canvas) {
			return;
		}
		const { width, height } = canvas;
		const graphEditor = graphManager.getGraphEditor();
		const cs = graphEditor.getCoordinateSystem();
		const graph = graphEditor.getGraph();
		// force layout
		graph.arrange(cs.deviceToLogX(width) / cs.getDeviceRatio(), cs.deviceToLogY(height) / cs.getDeviceRatio());
		const viewer = graphEditor.getGraphViewer();
		viewer.layout(cs.deviceToLogX(width) / cs.getDeviceRatio(), cs.deviceToLogY(height) / cs.getDeviceRatio());
		graphEditor.setScrollPosition(0, 0);
		graphEditor.resizeContent(width, height);
	};

	const addStreamSheet = (type) => {
		graphManager.getGraphViewer().getSelectionProvider().clearSelection();
		const options = {
			type,
			scope: props.user.scope,
			position: { x: 0, y: 0 }
		};
		props.createStreamSheet(props.machineId, 0, options);
	};

	const onInsertStreamSheet = (sht, container) => {
		const shtInsertAt = getSheetIndex(sht, container);
		const shtAddedTo = sheetContainer === container.leftSheetContainer ? 'left' : 'right';
		setShowTabContextMenu(false);
		setSheetAddedTo(shtAddedTo);
		setSheetInsertAt(shtInsertAt);
		addStreamSheet('sheet');
	};

	const selectTab = (index, tabContainer) => {
		tabContainer = tabContainer || sheetContainer;
		if (tabContainer) {
			const streamsheets = tabContainer.getStreamSheetsContainer().streamSheetsModel;
			tabContainer.getInboxCaption().connections =
				connectionStatus(props.connections, streamsheets[index].id);
			tabContainer.setActiveSheetIndex(index, graphManager.getGraphViewer());
			if (props.onSheetActivate) props.onSheetActivate(streamsheets[index].id);
		}
	};

	const onStreamSheetAdded = () => {
		if (sheetAddedTo.current) {
			const container = graphManager.getGraph().streamSheetsContainer;
			const tabContainer =
				sheetAddedTo.current === 'left' ? container.leftSheetContainer : container.rightSheetContainer;
			let index = container.streamSheetsModel.length - 1;
			if (sheetInsertAt.current != null) {
				graphManager.moveSheet(index, sheetInsertAt.current);
				index = sheetInsertAt.current;
			}
			selectTab(index, tabContainer);
			setSheetAddedTo();
			setSheetInsertAt();
		}
	};

	const updateTabContainer= (currIndex, sheetsContainer) => {
		const maxIndex = sheetsContainer.streamSheetsModel.length - 1;
		return (tabContainer) => {
			const activeIndex = tabContainer.activeSheetIndex;
			const newIndex = activeIndex < 0 ? Math.min(currIndex, maxIndex) : activeIndex;
			selectTab(newIndex, tabContainer);
		};
	};
	const onStreamSheetDeleted = (notification) => {
		const { index = 0 } = notification.object || {};
		const container = graphManager.getGraph().streamSheetsContainer;
		const updateTabSelection = updateTabContainer(index, container);
		updateTabSelection(container.leftSheetContainer);
		updateTabSelection(container.rightSheetContainer);
	};

	const triggerRerender = () => {
		setRandom(Math.random());
	};

	const onSheetSearch = () => {
		setSearchOpen(true);
		setTimeout(() => {
			document.getElementById('sheet-search').focus();
		}, 100);
	};

	const onSelectionChanged = (/* notification */) => {
		const activeSheet = graphManager.getActiveSheetModel();
		if (props.onSheetActivate && activeSheet) props.onSheetActivate(activeSheet.id);
	};

	const onShowCellPayload = (notification) => {
		const { div, ref: { row, col }, sheetId, onClose } = notification.object;
		setPayloadInfo({ open: true, anchor: div, reference: `${col}${row}`, sheetId, onClose });
	};
	const onHideCellPayload = () => {
		payloadInfo.onClose();
		setPayloadInfo({ open: false });
	};

	const onVerifyFormulas = (notification => {
		setShapeCommand(notification.object);
		setVerifyShapeFormulas(true);
	});

	const handleShapeCommand = () => {
		setVerifyShapeFormulas(false);
		const {cmd, completionfunc, handler} = shapeCommand;
		handler.execute(cmd, completionfunc, true);
	};

	useJSGNotification(JSG.NotificationCenter.ZOOM_NOTIFICATION, onZoom);
	useJSGNotification('show.cell.payload', onShowCellPayload);
	useJSGNotification('streamsheet_settings_notification', triggerRerender);
	useJSGNotification(JSG.WorksheetView.SHEET_SEARCH_NOTIFICATION, onSheetSearch);
	useJSGNotification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, onSelectionChanged);
	useJSGNotification(JSG.StreamSheetsContainer.STREAMSHEET_ADDED_NOTIFICATION, onStreamSheetAdded);
	useJSGNotification(JSG.StreamSheetsContainer.STREAMSHEET_DELETED_NOTIFICATION, onStreamSheetDeleted);
	useJSGNotification("ShapeFormulas.Overwrite", onVerifyFormulas);

	useEffect(() => {
		const resizeObserver = new ResizeObserver(updateDimensions);
		const el = document.getElementById(props.machineId);
		if (el) {
			// resize observer is a tool you can use to watch for size changes efficiently
			resizeObserver.observe(el);
		}

		return () => resizeObserver.disconnect();
	}, []);

	const showSearchCell = () => {
		const graph = graphManager.getGraph();
		if (!graph.searchResult || graph.activeSearchIndex === -1) {
			return;
		}
		const viewer = graphManager.getGraphViewer();
		const graphController = viewer.getGraphController();
		const searchRange = graph.searchResult[graph.activeSearchIndex];
		if (!searchRange) {
			return;
		}
		const index = graph.getSheetIndex(searchRange.sheet);
		graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(index, viewer);
		const sheetItem = graph.streamSheetsContainer.leftSheetContainer.streamSheet;

		const controller = graphController.getControllerByModelId(sheetItem.getId());
		if (!controller) {
			return;
		}

		const view = controller.getView();

		viewer.getGraphView().setFocus(controller);
		view.showCell({ x: searchRange.getX1(), y: searchRange.getY1() });

		// setSearchCount(graphManager.getGraph().searchResult ? graphManager.getGraph().searchResult.length : 0);
	};

	const handleSearch = (event) => {
		graphManager.getGraph().collectSearchResult(event.target.value, {
			matchCase: searchCaps,
			formulas: searchFormulas,
			activeSheet: searchActiveSheet,
			sheet: graphManager.getActiveSheetView() ? graphManager.getActiveSheetView().getItem() : undefined
		});

		setSearchText(event.target.value);
		showSearchCell();
		graphManager.redraw();
	};

	const handleSearchPrevious = () => {
		graphManager.getGraph().getPreviousSearchResult();
		showSearchCell();
		graphManager.redraw();
	};

	const handleSearchNext = () => {
		graphManager.getGraph().getNextSearchResult();
		showSearchCell();
		graphManager.redraw();
	};

	const handleSearchClose = () => {
		setSearchOpen(false);
		setSearchText('');

		const graph = graphManager.getGraph();
		if (!graph.searchResult || graph.activeSearchIndex === -1) {
			return;
		}
		const viewer = graphManager.getGraphViewer();
		const graphController = viewer.getGraphController();
		const searchRange = graph.searchResult[graph.activeSearchIndex];
		if (!searchRange) {
			return;
		}
		const index = graph.getSheetIndex(searchRange.sheet);
		graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(index, viewer);
		const sheetItem = graph.streamSheetsContainer.leftSheetContainer.streamSheet;

		const controller = graphController.getControllerByModelId(sheetItem.getId());
		if (!controller) {
			return;
		}
		const view = controller.getView();

		// viewer.getGraphView().setFocus(controller);

		const selection = view.getOwnSelection();
		selection.selectRange(searchRange.copy());
		view.showCell(selection.getActiveCell());
		view.notifySelectionChange(viewer);

		graphManager.getGraph().clearSearchResult();
		graphManager.redraw();
		setTimeout(() => {
			graphManager.getCanvas().focus();
		}, 100);
	};

	const handleSearchKeyDown = (event) => {
		switch (event.key) {
			case 'Enter':
			case 'ArrowDown':
				handleSearchNext();
				break;
			case 'Escape':
				handleSearchClose();
				break;
			case 'ArrowUp':
				handleSearchPrevious();
				break;
			default:
				break;
		}
	};

	const handleSearchOptions = () => {
		setSearchOptions(!searchOptions);
	};

	const handleSearchCaps = (event, state) => {
		setSearchCaps(state);
		graphManager.getGraph().collectSearchResult(searchText, {
			matchCase: state,
			formulas: searchFormulas,
			activeSheet: searchActiveSheet,
			sheet: graphManager.getActiveSheetView() ? graphManager.getActiveSheetView().getItem().model : undefined
		});
		showSearchCell();
		graphManager.redraw();
	};

	const handleSearchFormulas = (event, state) => {
		setSearchFormulas(state);
		graphManager.getGraph().collectSearchResult(searchText, {
			matchCase: searchCaps,
			formulas: state,
			activeSheet: searchActiveSheet,
			sheet: graphManager.getActiveSheetView() ? graphManager.getActiveSheetView().getItem().model : undefined
		});
		showSearchCell();
		graphManager.redraw();
	};

	const handleSearchActiveSheet = (event, state) => {
		setSearchActiveSheet(state);
		graphManager.getGraph().collectSearchResult(searchText, {
			matchCase: searchCaps,
			formulas: searchFormulas,
			activeSheet: state,
			sheet: graphManager.getActiveSheetView() ? graphManager.getActiveSheetView().getItem().model : undefined
		});
		showSearchCell();
		graphManager.redraw();
	};

	const handleSpeedOpenLeft = () => {
		setSpeedDialOpenLeft(true);
	};

	const handleSpeedOpenRight = () => {
		setSpeedDialOpenRight(true);
	};

	const handleSpeedCloseLeft = () => {
		setSpeedDialOpenLeft(false);
	};

	const handleSpeedCloseRight = () => {
		setSpeedDialOpenRight(false);
	};

	const addSheetAllowed = () => {
		const graph = graphManager.getGraph();
		if (!graph || !canEdit) {
			return false;
		}

		const sheetsAvailable = license.areSheetsAvailable(props.licenseInfo);
		const cnt = JSG.streamSheets.length;

		if (!sheetsAvailable) {
			return 'lic';
		} else if (cnt > 5) {
			return 'max';
		}
		return 'yes';
	};

	const onAdd = (type, left) => {
		addStreamSheet(type);
		setSpeedDialOpenLeft(false);
		setSpeedDialOpenRight(false);
		setSheetAddedTo(left ? 'left' : 'right');
	};

	const getAddTipTitle = (addAllowed, sht) => {
		switch (addAllowed) {
			default:
			case 'yes':
				return sht ? (
					<FormattedMessage id="Tooltip.AddStreamSheet" defaultMessage="Add StreamSheet" />
				) : (
					<FormattedMessage id="Tooltip.AddDashboard" defaultMessage="Add Dashboard" />
				);
			case 'lic':
				return (
					<FormattedMessage
						id="Alert.SheetMaximumLic"
						defaultMessage="Maximum number of Streamsheets reached!"
					/>
				);
			case 'max':
				return (
					<FormattedMessage
						id="Alert.SheetMaximum"
						defaultMessage="Maximum number of 6 Sheet per Machine allowed!"
					/>
				);
		}
	};

	const onCloseTabContextMenu = (event) => {
		event.preventDefault();
		setShowTabContextMenu(false);
	};

	const onTabContextMenu =
		({ tabIndex, tabSheet, tabSheetContainer, isRightClick }) =>
		(event) => {
			if (!showTabContextMenu && canEdit) {
				// select tab on right click
				if (isRightClick) {
					event.preventDefault();
					selectTab(tabIndex, tabSheetContainer);
					graphManager.redraw();
				}
				setShowTabContextMenu(true);
				setAnchorlEl(event.currentTarget);
				setSheet(tabSheet);
				setSheetContainer(tabSheetContainer);
			}
		};
	const onTabChange = (tabContainer) => (event, index) => {
		selectTab(index, tabContainer);
		graphManager.redraw();
		setRandom(Math.random());
	};

	const moveSheet = (sht, container, direction) => {
		const fromIndex = getSheetIndex(sht, container);
		const toIndex = getNextIndex(fromIndex, direction, container);
		graphManager.moveSheet(fromIndex, toIndex);
		selectTab(toIndex);
		setShowTabContextMenu(false);
	};

	const onMoveSheetLeft = (sht, container) => {
		if (hasLeftSheet(sht, container)) moveSheet(sht, container, -1);
	};

	const onMoveSheetRight = (sht, container) => {
		if (hasRightSheet(sht, container)) moveSheet(sht, container, 1);
	};

	const onHideSheet = (sht, container) => {
		let index = getSheetIndex(sht, container);
		if (hasLeftSheet(sht, container)) {
			index = getNextIndex(index, -1, container);
		} else {
			index = getNextIndex(index, 1, container);
		}
		graphManager.showSheet(sht, false);
		selectTab(index);
		setShowTabContextMenu(false);
	};

	const onDeleteStreamSheet = () => {
		if (JSG.streamSheets.length === 1) {
			JSG.NotificationCenter.getInstance().send(
				new JSG.Notification(JSG.WorksheetView.SHEET_MESSAGE_NOTIFICATION, {
					view: this,
					message: { message: intl.formatMessage({ id: 'Alert.SheetDelete' }, {}) }
				})
			);
			return;
		}

		setShowTabContextMenu(false);
		setShowDeleteSheetDialog(true);
	};

	const closeDeleteSheetDialog = () => {
		setShowDeleteSheetDialog(false);
	};

	const onShowDialog = (id) => {
		props.showDialog(id);
		setShowTabContextMenu(false);
	};

	const canAddStreamsheet = addSheetAllowed();
	const graph = graphManager.getGraph();
	let container;
	let dashboard;
	let outboxWidth = 0;
	let sheetHidden = false;

	if (graph) {
		dashboard = JSG.streamSheets.find((streamSheet) => streamSheet.properties.sheetType === 'dashboard');
		sheetHidden = JSG.streamSheets.find((streamSheet) => !streamSheet.properties.isVisible);
		JSG.connections = props.connections;
		container = graph.streamSheetsContainer;
		graph.viewSettings = viewSettings;
		graph.showViewMode = showViewMode;
		const machineContainer = graph.machineContainer;
		outboxWidth = machineContainer.outboxVisible
			? Math.round(
					graphManager
						.getGraphViewer()
						.getCoordinateSystem()
						.logToDeviceX(machineContainer.outboxContainer.getWidth().getValue(), false)
			  )
			: 0;

		graph.setProtected(!canEdit);
		container.leftSheetContainer.getInboxCaption().connections =
			connectionStatus(props.connections, container.leftSheetContainer.model.id);
		container.rightSheetContainer.getInboxCaption().connections =
			connectionStatus(props.connections, container.rightSheetContainer.model.id);
	}

	return (
		<div
			id="stream-app-edit-area"
			data-tour="stream-app-edit-area"
			style={{
				height: '100%',
				width: '100%'
			}}
		>
			<CellPayloadPopover
				open={payloadInfo.open}
				onClose={onHideCellPayload}
				anchor={payloadInfo.anchor}
				reference={payloadInfo.reference}
				streamsheetId={payloadInfo.sheetId}
			/>
			<SheetTabsList
				anchorEl={tabsListEl}
				open={!!tabsListEl}
				tabContainer={sheetContainer}
				onClose={() => setTabListEl()}
				selectTab={selectTab}
			/>
			{showViewMode || !canEdit ? null : (
				<React.Fragment>
					<Popover
						open={showTabContextMenu}
						anchorEl={anchorEl}
						anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
						transformOrigin={{ horizontal: 'left', vertical: 'top' }}
						onClose={onCloseTabContextMenu}
						onContextMenu={onCloseTabContextMenu}
						TransitionProps={
							{
								// onExited: handleFocus
							}
						}
					>
						<MenuList>
							<MenuItem dense key="streamSheetSettings" onClick={() => onShowDialog('SheetSettings')}>
								<FormattedMessage id="SheetComponent.Settings" defaultMessage="Settings" />
							</MenuItem>
							<Divider />
							<MenuItem
								dense
								key="insertStreamSheet"
								onClick={() => onInsertStreamSheet(sheet, container)}
							>
								<FormattedMessage id="SheetComponent.InsertSheet" defaultMessage="Insert" />
							</MenuItem>
							<MenuItem dense key="deleteStreamSheet" onClick={() => onDeleteStreamSheet()}>
								<FormattedMessage id="SheetComponent.DeleteSheet" defaultMessage="Delete Sheet" />
							</MenuItem>
							<MenuItem
								dense
								key="hideSheet"
								disabled={!canHideSheet(container)}
								onClick={() => onHideSheet(sheet, container)}
							>
								<FormattedMessage id="SheetComponent.HideSheet" defaultMessage="Hide" />
							</MenuItem>
							<Divider />
							<MenuItem
								dense
								key="moveSheetLeft"
								disabled={!hasLeftSheet(sheet, container)}
								onClick={() => onMoveSheetLeft(sheet, container)}
							>
								<FormattedMessage id="SheetComponent.MoveSheetLeft" defaultMessage="Move Left" />
							</MenuItem>
							<MenuItem
								dense
								key="moveSheetRight"
								disabled={!hasRightSheet(sheet, container)}
								onClick={() => onMoveSheetRight(sheet, container)}
							>
								<FormattedMessage id="SheetComponent.MoveSheetRight" defaultMessage="Move Right" />
							</MenuItem>
						</MenuList>
					</Popover>
					<SheetDeleteDialog
						machineId={props.machineId}
						scopeId={props.scope.id}
						sheet={sheet}
						open={showDeleteSheetDialog}
						close={closeDeleteSheetDialog}
					/>
					<SheetContextMenu showDialog={props.showDialog} />
					<TreeContextMenu />
					<GraphContextMenu />
					<LayoutContextMenu />
					<EditPointsContextMenu />
					<AlertDialog />
					<DecisionDialog />
					<ConfirmDialog
						onConfirm={handleShapeCommand}
						onCancel={() => {setVerifyShapeFormulas(false)}}
						open={verifyShapeFormulas}
						title={<FormattedMessage id="SheetComponent.VerifyFormulasTitle" defaultMessage="Overwrite Formulas" />}
						content={
							<FormattedMessage
								id="SheetComponent.VerifyFormulas"
								defaultMessage="Overwrite formulas in shape?"
							/>
						}
					/>
					<Paper
						elevation={4}
						style={{
							position: 'absolute',
							top: '10px',
							right: '10px',
							visibility: searchOpen ? 'visible' : 'hidden',
							paddingBottom: '6px'
						}}
					>
						<div
							style={{
								display: 'inline-flex',
								alignItems: 'center'
							}}
						>
							<TextField
								variant="outlined"
								inputProps={{
									id: 'sheet-search'
								}}
								size="small"
								margin="normal"
								style={{
									width: '180px',
									display: 'inline-block',
									marginLeft: '8px'
								}}
								label={
									graphManager.getGraph().searchResult ? (
										intl.formatMessage(
											{ id: 'SheetComponent.SearchResult' },
											{
												count: graphManager.getGraph().searchResult.length,
												index: graphManager.getGraph().activeSearchIndex + 1
											}
										)
									) : (
										<FormattedMessage id="SheetComponent.SearchText" defaultMessage="SearchText" />
									)
								}
								value={searchText}
								onKeyUp={handleSearchKeyDown}
								onChange={handleSearch}
							/>
							<IconButton
								style={{
									margin: '5px'
								}}
								size="small"
								onClick={(e) => handleSearchPrevious(e)}
								disabled={!graphManager.getGraph().searchResult}
							>
								<KeyboardArrowUpIcon />
							</IconButton>
							<IconButton
								style={{
									margin: '5px'
								}}
								size="small"
								onClick={(e) => handleSearchNext(e)}
								disabled={!graphManager.getGraph().searchResult}
							>
								<KeyboardArrowDownIcon />
							</IconButton>
							<IconButton
								style={{
									margin: '5px'
								}}
								size="small"
								onClick={(e) => handleSearchOptions(e)}
								disabled={false}
							>
								<MoreVertIcon />
							</IconButton>
							<IconButton
								style={{
									margin: '5px'
								}}
								size="small"
								onClick={(e) => handleSearchClose(e)}
								disabled={false}
							>
								<CloseIcon />
							</IconButton>
						</div>
						{searchOptions ? (
							<FormGroup
								style={{
									marginLeft: '6px'
								}}
							>
								<FormControlLabel
									control={
										<Checkbox
											checked={searchCaps}
											onChange={(event, state) => handleSearchCaps(event, state)}
										/>
									}
									label={
										<FormattedMessage id="SheetComponent.SearchCaps" defaultMessage="Match Case" />
									}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={searchFormulas}
											onChange={(event, state) => handleSearchFormulas(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="SheetComponent.SearchFormulas"
											defaultMessage="Search in Formulas"
										/>
									}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={searchActiveSheet}
											onChange={(event, state) => handleSearchActiveSheet(event, state)}
										/>
									}
									label={
										<FormattedMessage
											id="SheetComponent.SearchActiveSheet"
											defaultMessage="Search in active Sheet"
										/>
									}
								/>
							</FormGroup>
						) : null}
					</Paper>
				</React.Fragment>
			)}
			<CanvasHolder
				id={props.machineId}
				style={{
					width: `100%`,
					height: `calc(100% - ${showViewMode ? 0 : 2}px)`,
					marginTop: showViewMode ? '0px' : '7px'
				}}
			/>
			{showViewMode === false && graph && container
				? [
						<Box
							key="sheettabs"
							sx={{
								width: `calc(100% - ${outboxWidth}px)`,
								bgcolor: JSG.theme.graph,
								position: 'absolute',
								// bottom: '0px',
								height: '35px',
								display: 'flex'
							}}
						>
							<Box
								data-tour="sheet-tabs"
								key="sheettabsleft"
								sx={{
									width: `${props.splitView ? '50%' : '100%'}`,
									height: '34px',
									display: 'grid',
									flex: '1',
									gridTemplateColumns: `30px 30px 1fr 100px`,
									gridTemplateRows: '34px'
								}}
							>
								<React.Fragment>
									{dashboard || canAddStreamsheet !== 'yes' ? (
										<Tooltip enterDelay={300} title={getAddTipTitle(canAddStreamsheet, true)}>
											<div>
												<IconButton
													id="addSheetLeft"
													aria-label="add"
													size="small"
													disabled={canAddStreamsheet !== 'yes'}
													sx={{
														bgcolor: JSG.theme.graph,
														color: 'text.primary',
														minHeight: '30px',
														height: '30px',
														minWidth: '30px',
														width: '30px',
														marginTop: '2px'
													}}
													onClick={() => onAdd('sheet', true)}
												>
													<AddIcon />
												</IconButton>
											</div>
										</Tooltip>
									) : (
										<SpeedDial
											style={{
												zIndex: 1200,
												width: '30px',
												height: '33px',
												minWidth: '30px'
											}}
											ariaLabel="Add"
											FabProps={{
												size: 'small',
												sx: {
													bgcolor: JSG.theme.graph,
													color: 'text.primary',
													boxShadow: 0,
													minHeight: '30px',
													height: '30px',
													minWidth: '30px',
													width: '30px'
												}
											}}
											icon={<SpeedDialIcon />}
											onClose={handleSpeedCloseLeft}
											onOpen={handleSpeedOpenLeft}
											open={speedDialOpenLeft}
											direction="up"
										>
											<SpeedDialAction
												key="sheetLeft"
												icon={<SheetIcon />}
												tooltipTitle={getAddTipTitle(canAddStreamsheet, true)}
												onClick={() => onAdd('sheet', true)}
											/>
											<SpeedDialAction
												key="dashLeft"
												icon={<DashboardIcon />}
												tooltipTitle={getAddTipTitle(canAddStreamsheet, false)}
												onClick={() => onAdd('dashboard', true)}
											/>
										</SpeedDial>
									)}
								</React.Fragment>
								<IconButton
									size="small"
									onClick={(ev) => {
										setTabListEl(ev.currentTarget);
										setSheetContainer(container.leftSheetContainer);
									}}
									disabled={false}
								>
									{sheetHidden ? (
										<SvgIcon>
											<path d="M3,15H11V13H3V15M3,19H10V17H3V19M3,11H21V9H3V11M3,5V7H21V5H3 M12.38 12.73L12.72 14.61A6.21 6.21 0 0 0 11 17A6.45 6.45 0 0 0 17 21A6.55 6.55 0 0 0 18.84 20.7L22.11 21.46L20.58 19.93M17 19.5A2.5 2.5 0 0 1 14.56 16.45L17.56 19.45A2.5 2.5 0 0 1 17 19.5M16.24 13L17.85 14.61A2.5 2.5 0 0 1 19.35 16.11L21.94 18.7A6.44 6.44 0 0 0 23 17A6.45 6.45 0 0 0 17 13H16.24Z" />
										</SvgIcon>
									) : (
										<ReorderIcon />
									)}
								</IconButton>
								<Tabs
									style={{
										minHeight: '35px'
									}}
									variant="scrollable"
									value={container.leftSheetContainer.activeSheetIndex}
									onChange={onTabChange(container.leftSheetContainer)}
									scrollButtons
								>
									{container.streamSheetsModel.map((sht, index) =>
										sht.isVisible ? (
											<Tab
												sx={{
													minHeight: '34px',
													padding: '0px'
												}}
												icon={
													canEdit ? (
														<ArrowDropDown
															onClick={onTabContextMenu({
																tabIndex: index,
																tabSheet: sht,
																tabSheetContainer: container.leftSheetContainer
															})}
														/>
													) : null
												}
												iconPosition="end"
												label={sht.name}
												value={index}
												key={`a${index.toString()}`}
												onContextMenu={onTabContextMenu({
													tabIndex: index,
													tabSheet: sht,
													tabSheetContainer: container.leftSheetContainer,
													isRightClick: true
												})}
											/>
										) : null
									)}
								</Tabs>
								<Tooltip enterDelay={300} title={container.leftSheetContainer.stepTimeStampFormatted}>
									<div>
										<Typography color="text.primary" sx={{ lineHeight: '34px', marginRight: '10px' }}>
											<span id="leftSteps" style={{ whiteSpace: 'nowrap' }}>
												{intl.formatMessage(
													{ id: `Steps` },
													{ step: container.leftSheetContainer.sheetSteps }
												)}
											</span>
										</Typography>
									</div>
								</Tooltip>
							</Box>
							{props.splitView ? (
								<Box
									key="sheettabsright"
									sx={{
										width: '50%',
										height: '34px',
										display: 'grid',
										flex: '1',
										gridTemplateColumns: `30px 30px 1fr 100px`,
										gridTemplateRows: '34px'
									}}
								>
									<React.Fragment>
										{dashboard || canAddStreamsheet !== 'yes' ? (
											<Tooltip enterDelay={300} title={getAddTipTitle(canAddStreamsheet, true)}>
												<div>
													<IconButton
														id="addSheetRight"
														aria-label="add"
														size="small"
														disabled={canAddStreamsheet !== 'yes'}
														sx={{
															bgcolor: JSG.theme.graph,
															color: 'text.primary',
															minHeight: '30px',
															height: '30px',
															minWidth: '30px',
															width: '30px',
															marginTop: '2px'
														}}
														onClick={() => onAdd('sheet', false)}
													>
														<AddIcon />
													</IconButton>
												</div>
											</Tooltip>
										) : (
											<SpeedDial
												style={{
													zIndex: 1200,
													width: '30px',
													height: '33px',
													minWidth: '30px'
												}}
												ariaLabel="Add"
												FabProps={{
													size: 'small',
													sx: {
														bgcolor: JSG.theme.graph,
														color: 'text.primary',
														boxShadow: 0,
														minHeight: '30px',
														height: '30px',
														minWidth: '30px',
														width: '30px'
													}
												}}
												icon={<SpeedDialIcon />}
												onClose={handleSpeedCloseRight}
												onOpen={handleSpeedOpenRight}
												open={speedDialOpenRight}
												direction="up"
											>
												<SpeedDialAction
													key="sheetRight"
													icon={<SheetIcon />}
													tooltipTitle={getAddTipTitle(canAddStreamsheet, true)}
													onClick={() => onAdd('sheet', false)}
												/>
												<SpeedDialAction
													key="dashRight"
													icon={<DashboardIcon />}
													tooltipTitle={getAddTipTitle(canAddStreamsheet, false)}
													onClick={() => onAdd('dashboard', false)}
												/>
											</SpeedDial>
										)}
									</React.Fragment>
									<IconButton
										style={{ width: '30px' }}
										size="small"
										onClick={(ev) => {
											setTabListEl(ev.currentTarget);
											setSheetContainer(container.rightSheetContainer);
										}}
										disabled={false}
									>
										{sheetHidden ? (
											<SvgIcon>
												<path d="M3,15H11V13H3V15M3,19H10V17H3V19M3,11H21V9H3V11M3,5V7H21V5H3 M12.38 12.73L12.72 14.61A6.21 6.21 0 0 0 11 17A6.45 6.45 0 0 0 17 21A6.55 6.55 0 0 0 18.84 20.7L22.11 21.46L20.58 19.93M17 19.5A2.5 2.5 0 0 1 14.56 16.45L17.56 19.45A2.5 2.5 0 0 1 17 19.5M16.24 13L17.85 14.61A2.5 2.5 0 0 1 19.35 16.11L21.94 18.7A6.44 6.44 0 0 0 23 17A6.45 6.45 0 0 0 17 13H16.24Z" />
											</SvgIcon>
										) : (
											<ReorderIcon />
										)}
									</IconButton>
									<Tabs
										style={{
											minHeight: '35px'
										}}
										variant="scrollable"
										value={container.rightSheetContainer.activeSheetIndex}
										onChange={onTabChange(container.rightSheetContainer)}
										scrollButtons
									>
										{container.streamSheetsModel.map((sht, index) =>
											sht.isVisible ? (
												<Tab
													sx={{
														minHeight: '34px',
														padding: '0px'
													}}
													icon={
														canEdit ? (
															<ArrowDropDown
																onClick={onTabContextMenu({
																	tabIndex: index,
																	tabSheet: sht,
																	tabSheetContainer: container.rightSheetContainer
																})}
															/>
														) : null
													}
													iconPosition="end"
													label={sht.name}
													value={index}
													key={`b${index.toString()}`}
													onContextMenu={onTabContextMenu({
														tabIndex: index,
														tabSheet: sht,
														tabSheetContainer: container.rightSheetContainer,
														isRightClick: true
													})}
												/>
											) : null
										)}
									</Tabs>
									<Tooltip enterDelay={300} title={container.rightSheetContainer.stepTimeStampFormatted}>
										<div>
											<Typography color="text.primary" sx={{ lineHeight: '34px', marginRight: '10px' }}>
												<span id="rightSteps" style={{ whiteSpace: 'nowrap' }}>
													{intl.formatMessage(
														{ id: `Steps` },
														{ step: container.rightSheetContainer.sheetSteps }
													)}
												</span>
											</Typography>
										</div>
									</Tooltip>
								</Box>
							) : null}
						</Box>
				  ]
				: null}
		</div>
	);
}


function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export default withAppData(({ machine, canEditApp, connections }) => ({
	connections,
	canEditMachine: canEditApp,
	viewSettings: machine.settings.view,
	machineId: machine.id,
	scope: machine.scope
}))(withUser(({ user, licenseInfo }) => ({ user, licenseInfo }))(connect(null, mapDispatchToProps)(SheetComponent)));
